/* eslint-disable react/button-has-type */
/* eslint-disable no-unused-vars */
import React, { forwardRef } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import {
  Box,
  Button,
  InputBase,
  MenuItem,
  Select,
  FormControl,
  OutlinedInput,
  ButtonGroup,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";
import { useSelector, useDispatch } from "react-redux";
import { selectDateFilter } from "../../redux/selectors/dateFilterSelector";
import { setDateRange } from "../../redux/features/dateFilter/action";
import { getStartOfDay, getEndOfDay } from "../../utils/utils";
import ExportDropdown from "../ExportDropdown/ExportDropdown";
import styles from "./Header.module.css";
import { ROUTES } from "../../constants";
import { VIEW_TYPE } from "../../utils/ticketUtils";

// Custom input for date range picker
const CustomDateInput = forwardRef(
  ({ value, onClick, className, startDate, endDate }, ref) => {
    const formatDate = (date) =>
      date ? format(date, "dd MMM yyyy") : format(new Date(), "dd MMM yyyy");

    const handleKeyDown = (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    };

    return (
      <div
        ref={ref}
        className={className}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          cursor: "pointer",
        }}
      >
        <strong>{formatDate(startDate)}</strong>
        <span>to</span>
        <strong>{formatDate(endDate)}</strong>
        <div
          style={{
            padding: "2px",
            textDecoration: "underline",
            color: "#007bff",
          }}
        >
          Change Date
        </div>
      </div>
    );
  }
);

CustomDateInput.displayName = "CustomDateInput";

CustomDateInput.propTypes = {
  value: PropTypes.string,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  onClick: PropTypes.func.isRequired,
  className: PropTypes.string,
};

const statusOptions = [
  { label: "All", value: "ALL" },
  { label: "Pending with Performer", value: "RTA_APPROVED" },
  { label: "Pending with Approver", value: "PERFORMER_APPROVED" },
  { label: "Pending with RTA", value: "RTA_RECTIFY" },
  { label: "Pending EL", value: "APPROVER_APPROVED" },
    { label: "e-Verification In Progress", value: "SRN_UPDATE" },

  { label: "Rejected By RTA", value: "RTA_REJECT" },
  { label : "Closed Ticket", value:"CLOSED"}
];

const Header = ({
  searchQuery = "",
  handleSearchQuery,
  onRefresh,
  isRefreshing = false,
  onExport,
  isExporting = false,
  TicketStatus,
  setTicketStatus,
  showViewToggle = false,
  selectedView = "open",
  onViewChange,
  viewOptions,
  disableExport = false,
}) => {
  const { startDate, endDate } = useSelector(selectDateFilter);
  const dispatch = useDispatch();
  const location = useLocation();
  
  const handleStatusChange = (event) => {
    const selectedStatus = event.target.value;
    setTicketStatus?.(selectedStatus);
  };

  const getSelectedStatusLabel = (selectedValue) => {
    if (!selectedValue) return <em>Select Status</em>;
    const selectedOption = statusOptions.find(
      (status) => status.value === selectedValue
    );
    return selectedOption ? selectedOption.label : <em>Select Status</em>;
  };

  const handleDateChange = (dates) => {
    if (Array.isArray(dates) && dates.length === 2) {
      // const [start, end] = dates;
      const start = dates[0];
      let end = dates[1];
       const today = new Date();

    // Cap future end date
    if (end && end > today) {
      end = today;
    }
      dispatch(setDateRange(getStartOfDay(start), getEndOfDay(end)));

    }
  };

  const handleViewChange = (view) => {
    onViewChange?.(view);
  };

  return (
    <Box className={styles.headerWrapper}>
      {/* View Toggle ButtonGroup */}
      {showViewToggle && viewOptions && (
        <Box style={{ display: "flex", justifyContent: "flex-start" }}>
          <ButtonGroup variant="outlined" aria-label="view selection">
            {viewOptions.map((option) => (
              <Button
                key={option.value}
                onClick={() => handleViewChange(option.value)}
                variant={
                  selectedView === option.value ? "contained" : "outlined"
                }
                color={selectedView === option.value ? "primary" : "default"}
                style={{
                  backgroundColor:
                    selectedView === option.value ? "#0F3983" : "transparent",
                  color: selectedView === option.value ? "#fff" : "#0F3983",
                }}
              >
                {option.label}
              </Button>
            ))}
          </ButtonGroup>
        </Box>
      )}

      {/* Date Range Picker */}

      <Box>
        <DatePicker
          selected={startDate}
          maxDate={new Date()}
          onChange={handleDateChange}
          startDate={startDate}
          endDate={endDate}
          selectsRange
          customInput={
            <CustomDateInput startDate={startDate} endDate={endDate} />
          }
          dateFormat="dd MMM yyyy"
          placeholderText="Select date range"
        />
      </Box>


      {/* Search Input */}
      <Box className={styles.searchContainer}>
        <SearchIcon />
        <InputBase
          placeholder="Search here..."
          value={searchQuery}
          onChange={(e) => handleSearchQuery?.(e.target.value)}
          className={styles.searchInput}
          inputProps={{ "aria-label": "search" }}
        />
      </Box>

      {/* Action Buttons */}
      <Box className={styles.buttonGroup}>
        {/* Status Selector - Only for RTA Rectify Route */}
        {TicketStatus && setTicketStatus && (
          <FormControl
            sx={{ minWidth: 180, background: "#fff" }}
            size="small"
            variant="outlined"
          >
            <Select
              displayEmpty
              value={TicketStatus}
              onChange={handleStatusChange}
              input={<OutlinedInput style={{ height: 36 }} />}
              renderValue={(selected) => getSelectedStatusLabel(selected)}
              inputProps={{ "aria-label": "Select Status" }}
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "right",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "right",
                },
                getContentAnchorEl: null,
                PaperProps: {
                  style: {
                    maxHeight: 48 * statusOptions.length + 8,
                    width: 250,
                  },
                },
              }}
            >
              {statusOptions.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Button
          variant="outlined"
          onClick={() => onRefresh?.()}
          disabled={isRefreshing}
          startIcon={<RefreshIcon />}
          aria-label="refresh data"
        >
          {isRefreshing ? "Refreshing..." : "Refresh"}
        </Button>

        <ExportDropdown onExport={onExport} isExporting={isExporting} disabled={disableExport} />
      </Box>
    </Box>
  );
};

Header.propTypes = {
  searchQuery: PropTypes.string,
  handleSearchQuery: PropTypes.func,
  onRefresh: PropTypes.func,
  isRefreshing: PropTypes.bool,
  onExport: PropTypes.func.isRequired,
  isExporting: PropTypes.bool,
  TicketStatus: PropTypes.string,
  setTicketStatus: PropTypes.func,
  showViewToggle: PropTypes.bool,
  selectedView: PropTypes.oneOf([VIEW_TYPE.OPEN, VIEW_TYPE.ASSIGNED]),
  onViewChange: PropTypes.func,
  viewOptions: PropTypes.object,
  disableExport: PropTypes.bool,
};

export default Header;
